import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';

// Initialize dayjs plugins
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.extend(utc);

export const WEEK_WIDTH = 120; // Width for each week column (3 times the original day width)

// Keep DAY_WIDTH for backward compatibility in calculations
export const DAY_WIDTH = WEEK_WIDTH / 7;

// Week utility functions
export const getWeekStart = (date: Date) => {
  return dayjs(date).startOf('isoWeek').toDate(); // Monday as start of week
};

export const getWeekEnd = (date: Date) => {
  return dayjs(date).endOf('isoWeek').toDate();
};

export const generateWeekHeaders = (minDate: Date, maxDate: Date) => {
  const weeks: Date[] = [];
  const currentWeek = dayjs(getWeekStart(minDate));
  const endWeek = dayjs(getWeekStart(maxDate));

  let week = currentWeek;
  while (week.isBefore(endWeek) || week.isSame(endWeek, 'day')) {
    weeks.push(week.toDate());
    week = week.add(1, 'week');
  }

  return weeks;
};
export const getWeekOffset = (date: Date, minDate: Date) => {
  const startWeek = dayjs(getWeekStart(minDate));
  const targetWeek = dayjs(getWeekStart(date));
  return targetWeek.diff(startWeek, 'week');
};

export const formatWeekHeader = (weekStart: Date) => {
  const weekEnd = getWeekEnd(weekStart);
  return `${dayjs(weekStart).format('MMM DD')} - ${dayjs(weekEnd).format('DD')}`;
};

// Day grid utility functions for debugging
export const generateDayGridLines = (minDate: Date, maxDate: Date) => {
  const days: Date[] = [];
  const currentDay = dayjs(minDate);
  const endDay = dayjs(maxDate);

  let day = currentDay;
  while (day.isBefore(endDay) || day.isSame(endDay, 'day')) {
    days.push(day.toDate());
    day = day.add(1, 'day');
  }

  return days;
};

export const getDayOffsetFromMinDate = (date: Date, minDate: Date) => {
  return dayjs(date).diff(dayjs(minDate), 'day');
};

// Debug utility to understand week-month alignment
export const debugWeekMonth = (weekHeaders: Date[]) => {
  return weekHeaders.map((weekStart, index) => {
    const weekEnd = dayjs(weekStart).endOf('isoWeek').toDate();
    const currentMonth = dayjs(weekStart).month();
    const previousMonth =
      index > 0 ? dayjs(weekHeaders[index - 1]).month() : -1;
    const isNewMonth = index === 0 || currentMonth !== previousMonth;

    return {
      index,
      weekStart: dayjs(weekStart).format('MMM DD (ddd)'),
      weekEnd: dayjs(weekEnd).format('MMM DD (ddd)'),
      month: dayjs(weekStart).format('MMMM'),
      isNewMonth,
      currentMonth,
      previousMonth,
    };
  });
};

// Generate weekend days (Saturday and Sunday) for highlighting
export const generateWeekendDays = (minDate: Date, maxDate: Date) => {
  const weekendDays: Date[] = [];
  const currentDay = dayjs(minDate);
  const endDay = dayjs(maxDate);

  let day = currentDay;
  while (day.isBefore(endDay) || day.isSame(endDay, 'day')) {
    // Check if day is Saturday (6) or Sunday (0)
    if (day.day() === 0 || day.day() === 6) {
      weekendDays.push(day.toDate());
    }
    day = day.add(1, 'day');
  }

  return weekendDays;
};
